import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef, useContext, PointerEvent as ReactPointerEvent } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from '../../contexts/LayoutContext';

import DetailFrame from '../shared/Frames/DetailFrame';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { translate } from '../../i18n/translate';
import {
  PiPlayLight,
  PiStopCircleLight,
  PiPlusLight,
  PiDownloadSimpleLight,
  PiTrashLight,
  PiUploadSimpleLight,
  PiFloppyDiskLight,
  PiSpinnerGapLight,
  PiFileTextLight,
  PiFilePdfLight,
  PiFileDocLight,
  PiFileXlsLight,
  PiFileCsvLight,
  PiWarningLight,
  PiXLight,
  PiTableLight,
  PiArrowCounterClockwiseLight,
  PiTextTLight,
  PiCalendarBlankLight,
  PiHashLight,
  PiToggleLeftLight,
  PiCurrencyDollarLight,
  PiDiamondLight,
  PiColumnsLight,
  PiUsersLight,
  PiChatTextLight,
  PiPencilSimpleLight,
  PiClockCounterClockwiseLight,
  PiShieldCheckLight,
  PiEyeLight,
  PiArrowsClockwiseLight,
  PiGlobeLight,
  PiLockLight,
  PiLockOpenLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiWarningCircleLight,
  PiMinusCircleLight,
  PiTargetLight,
  PiSparkleLight,
} from 'react-icons/pi';

import {
  fetchBatchTableDetail,
  addColumn,
  updateColumn,
  deleteColumn,
  deleteDocument,
  startProcessing,
  stopProcessing,
  continueProcessing,
  retryFailed,
  exportTable,
  saveTableAsTemplate,
  updateBatchTable,
  resetTable,
  deleteBatchTable,
  streamTableDocument,
  processColumn,
  syncPlaybook,
  addTableCollaborator,
  removeTableCollaborator,
  updateTableCollaboratorAccess,
} from '../../store/actions/BatchTables/BatchTablesActions';

import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { TableDetail, TableColumn, TableRow, TableCell, TableStatus, ColumnDataType, UpdateColumnPayload, ComplianceStatus, RiskLevel, SyncPlaybookResult } from '../../types/batchTables';
import TableEmptyState from './TableEmptyState';
import CellDetailPanel from './CellDetailPanel';
import BulkReviewPanel from './BulkReviewPanel';
import EditColumnContent from './EditColumnContent';
import { useTablePubSub } from './useTablePubSub';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import { TABLE_SURFACE, useTableWorkspace } from './surface';
import {
  TableSurfaceProvider,
  cellSelectionId,
  parseCellSelectionId,
} from './surface/TableSurfaceContext';
import { Tooltip } from '@mui/material';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import AIUsagePanel from '../shared/aiUsage/AIUsagePanel';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tableDetailTourSteps } from '../shared/FeatureTour';
import { formatDateTime } from '../../utils/functions';
import Spinner from '../shared/TailwindComponents/Spinner';

// ─── Status colors ───────────────────────────────────────────────────────────

const statusColorMap: Record<TableStatus, BadgeColor> = {
  pending: 'gray',
  queued: 'orange',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  partial: 'yellow',
  stopped: 'amber',
};

// ─── Column type icon mapping ────────────────────────────────────────────────

const columnTypeIcon: Record<ColumnDataType, React.ReactNode> = {
  text: <PiTextTLight className="w-3.5 h-3.5" />,
  date: <PiCalendarBlankLight className="w-3.5 h-3.5" />,
  number: <PiHashLight className="w-3.5 h-3.5" />,
  boolean: <PiToggleLeftLight className="w-3.5 h-3.5" />,
  currency: <PiCurrencyDollarLight className="w-3.5 h-3.5" />,
};

// ─── File type icon ──────────────────────────────────────────────────────────

const getFileIcon = (fileType: string | null): React.ReactNode => {
  const ext = (fileType || '').toLowerCase();
  if (ext === 'pdf') return <PiFilePdfLight className="w-4 h-4 text-danger-500 flex-shrink-0" />;
  if (ext === 'docx' || ext === 'doc') return <PiFileDocLight className="w-4 h-4 text-info-500 flex-shrink-0" />;
  if (ext === 'xlsx' || ext === 'xls') return <PiFileXlsLight className="w-4 h-4 text-success-600 flex-shrink-0" />;
  if (ext === 'csv') return <PiFileCsvLight className="w-4 h-4 text-success-500 flex-shrink-0" />;
  return <PiFileTextLight className="w-4 h-4 text-gray-400 flex-shrink-0" />;
};

// ─── Smart value rendering ───────────────────────────────────────────────────

const isBooleanValue = (val: string): boolean => {
  const v = val.trim().toLowerCase();
  return ['true', 'false', 'yes', 'no'].includes(v);
};

const isTruthyBoolean = (val: string): boolean => {
  const v = val.trim().toLowerCase();
  return v === 'true' || v === 'yes';
};

// "Not found" (and close variants) is the AI's way of saying it couldn't
// locate the value. We de-emphasize it so the cells that DID find something
// stand out (#2231).
const isNotFoundValue = (val: string): boolean => {
  const v = val.trim().toLowerCase();
  return v === 'not found' || v === 'none found' || v === 'not specified';
};

const CellValueRenderer: React.FC<{ value: string | null; dataType?: ColumnDataType }> = ({ value, dataType }) => {
  if (!value) return <span className="text-gray-300 dark:text-gray-600">—</span>;

  // Boolean detection (explicit type or auto-detect).
  // Rendered as a NEUTRAL chip with no color/icon: red + flag on "No" was
  // ambiguous because "No" is positive for some questions ("Are there risks?")
  // and negative for others ("Are risks addressed?"). (#2232)
  if (dataType === 'boolean' || isBooleanValue(value)) {
    const truthy = isTruthyBoolean(value);
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-sunken text-fg-body">
        {truthy ? translate('Yes') : translate('No')}
      </span>
    );
  }

  // De-emphasized "Not found" so found information stands out more (#2231).
  if (isNotFoundValue(value)) {
    return (
      <span className="line-clamp-1 text-gray-400 dark:text-gray-600 text-xs italic leading-relaxed">
        {value}
      </span>
    );
  }

  // Default text — uniform style for all non-boolean values
  return (
    <span className="line-clamp-1 text-gray-700 dark:text-gray-300 text-xs leading-relaxed">
      {value}
    </span>
  );
};

// ─── Skeleton loader for processing cells ────────────────────────────────────

// Single one-line bar so processing rows keep the uniform ROW_H height (a
// 2-line skeleton made rows taller than ROW_H and broke the windowing math).
// Fixed width (no Math.random) to avoid width jitter across re-renders.
const CellSkeleton: React.FC = () => (
  <div className="flex items-center" style={{ height: 16 }}>
    <div
      className="h-3 rounded-full animate-skeleton-shimmer w-[70%]"
      style={{
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
      }}
    />
  </div>
);

// ─── Compliance helpers ───────────────────────────────────────────────────────

interface ComplianceStatusStyle {
  label: string;
  badgeBg: string;
  badgeText: string;
  cellTint: string;       // subtle full-cell tint, used as the scannable heatmap layer
  Icon: React.ComponentType<{ className?: string }>;
}

const complianceStatusConfig: Record<ComplianceStatus, ComplianceStatusStyle> = {
  compliant: {
    label: 'Compliant',
    badgeBg: 'bg-success-100/80 dark:bg-success-900/30',
    badgeText: 'text-success-800 dark:text-success-300',
    cellTint: 'bg-success-50/40 dark:bg-success-950/15',
    Icon: PiCheckCircleLight,
  },
  non_compliant: {
    label: 'Non-Compliant',
    badgeBg: 'bg-danger-100/80 dark:bg-danger-900/30',
    badgeText: 'text-danger-800 dark:text-danger-300',
    cellTint: 'bg-danger-50/50 dark:bg-danger-950/20',
    Icon: PiXCircleLight,
  },
  needs_review: {
    label: 'Review',
    badgeBg: 'bg-warning-100/80 dark:bg-warning-900/30',
    badgeText: 'text-warning-800 dark:text-warning-300',
    cellTint: 'bg-warning-50/50 dark:bg-warning-950/20',
    Icon: PiWarningCircleLight,
  },
  not_found: {
    label: 'Not Found',
    badgeBg: 'bg-surface-sunken/70',
    badgeText: 'text-gray-600 dark:text-gray-400',
    cellTint: 'bg-surface-sunken/60',
    Icon: PiMinusCircleLight,
  },
};

const riskLevelConfig: Record<RiskLevel, { label: string; color: string; stripe: string }> = {
  critical: { label: 'Critical', color: 'text-danger-600 dark:text-danger-400',     stripe: 'bg-danger-500' },
  major:    { label: 'Major',    color: 'text-warning-600 dark:text-warning-400', stripe: 'bg-warning-500' },
  minor:    { label: 'Minor',    color: 'text-warning-600 dark:text-warning-400',  stripe: 'bg-warning-400' },
  info:     { label: 'Info',     color: 'text-info-600 dark:text-info-400',    stripe: 'bg-info-400' },
};

const riskWeightConfig = (weight: number | null | undefined): string => {
  if (weight == null) return 'bg-surface-sunken text-gray-500';
  if (weight >= 9) return 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-400';
  if (weight >= 6) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  if (weight >= 3) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  return 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-400';
};

// Compliance status priority for row aggregate (worst = highest priority)
const compliancePriority: Record<ComplianceStatus, number> = {
  non_compliant: 4,
  needs_review: 3,
  not_found: 2,
  compliant: 1,
};

const getRowWorstComplianceStatus = (
  row: TableRow,
  columns: TableColumn[]
): ComplianceStatus | null => {
  const complianceCols = columns.filter((c) => c.column_type === 'compliance');
  if (complianceCols.length === 0) return null;

  let worst: ComplianceStatus | null = null;
  for (const col of complianceCols) {
    const cell = row.cells[String(col.id)];
    const status = cell?.compliance_status;
    if (!status) continue;
    if (!worst || compliancePriority[status] > compliancePriority[worst]) {
      worst = status;
    }
  }
  return worst;
};

// Default column widths
const DEFAULT_DOC_COL_WIDTH = 260;
const DEFAULT_QUERY_COL_WIDTH = 220;
const MIN_COL_WIDTH = 100;

// ─── Virtualization constants ─────────────────────────────────────────────────
// Every row is forced to this uniform, compact one-line height. Cells render a
// single truncated line (full value on hover via tooltip), so content never
// exceeds ROW_H — keeping rows consistent AND making the spacer-row windowing
// math exact (scrollTop maps cleanly to a row index).
const ROW_H = 40;
const OVERSCAN = 8;
// Below this row count we don't window at all — render every (uniform-height)
// row. Memoization keeps it cheap; windowing only kicks in for large tables.
const VIRTUALIZE_THRESHOLD = 100;

// ─── Memoized table row ───────────────────────────────────────────────────────
// Extracted from the parent map so a single row re-renders ONLY when its own
// `row` (or a primitive prop) changes. The reducer keeps unchanged row objects
// referentially stable across cell_completed PubSub updates, so this kills the
// full-table re-render (and loader flicker) on every live cell event. Only
// primitives or referentially-stable props are passed in.
interface BatchTableRowViewProps {
  row: TableRow;
  rowIndex: number;
  columns: TableColumn[];
  isComplianceTable: boolean;
  isActive: boolean;
  isDocColLocked: boolean;
  isSelected: boolean;
  selectedCellRowId: number | null;
  selectedCellColId: number | null;
  getColWidth: (colKey: string, defaultWidth: number) => number;
  handleToggleRow: (rowId: number) => void;
  handleOpenDocument: (row: TableRow) => void;
  handleDeleteDocument: (rowId: number) => void;
  /** Opens the shared detail pane on one cell. Referentially stable. */
  onSelectCell: (rowId: number, columnId: number) => void;
  t: (key: string) => string;
  /** When set (windowed/virtualized mode) the row is forced to this uniform
   *  height so spacer-row math is exact. Omitted = natural content height. */
  rowHeight?: number;
}

const BatchTableRowView: React.FC<BatchTableRowViewProps> = ({
  row,
  rowIndex,
  columns,
  isComplianceTable,
  isActive,
  isDocColLocked,
  isSelected,
  selectedCellRowId,
  selectedCellColId,
  getColWidth,
  handleToggleRow,
  handleOpenDocument,
  handleDeleteDocument,
  onSelectCell,
  t,
  rowHeight,
}) => {
  const isRowSelected = isSelected;
  const isRowProcessing = row.status === 'processing';
  const isRowFailed = row.status === 'failed';
  const worstStatus = isComplianceTable ? getRowWorstComplianceStatus(row, columns) : null;
  const worstConfig = worstStatus ? complianceStatusConfig[worstStatus] : null;

  // Per-row snapshot of each cell's status on the PREVIOUS render. A cell only
  // gets the "just completed" highlight when it transitions to completed while
  // mounted (a live PubSub update) — NOT when it mounts already-completed (which
  // is what happens when you scroll a virtualized row into view, and what made
  // already-done cells flash blue on scroll). On first mount the map is empty,
  // so nothing animates.
  const prevCellStatuses = useRef<Record<string, string | undefined>>({});
  useEffect(() => {
    const next: Record<string, string | undefined> = {};
    for (const col of columns) next[String(col.id)] = row.cells[String(col.id)]?.status;
    prevCellStatuses.current = next;
  });
  return (
    <tr
      style={rowHeight ? { height: rowHeight } : undefined}
      className={`transition-colors group/row ${
        isRowSelected
          ? 'bg-info-50/80 dark:bg-info-900/20'
          : isRowFailed
            ? 'bg-danger-50/50 dark:bg-danger-900/10'
            : worstStatus === 'non_compliant'
              ? 'bg-danger-50/40 dark:bg-danger-900/10 hover:bg-danger-50/60 dark:hover:bg-danger-900/20'
              : worstStatus === 'needs_review'
                ? 'bg-warning-50/40 dark:bg-warning-900/10 hover:bg-warning-50/60 dark:hover:bg-warning-900/20'
                : 'bg-surface/[0.55] hover:bg-surface/85'
      }`}
    >
      {/* Checkbox */}
      <td
        className={`${isDocColLocked ? 'bg-surface' : 'bg-surface/[0.55]'} px-1 py-2 border-r border-hairline-soft text-center w-[36px]`}
        style={isDocColLocked ? { position: 'sticky', left: 0, zIndex: 2 } : undefined}
      >
        <CheckBox
          label={t('Select row')}
          hideLabel
          checked={isRowSelected}
          onChange={() => handleToggleRow(row.id)}
        />
      </td>

      {/* Row number */}
      <td
        className={`${isDocColLocked ? 'bg-surface' : 'bg-surface/[0.55]'} px-1 py-2 border-r border-hairline-soft text-center w-[32px]`}
        style={isDocColLocked ? { position: 'sticky', left: 36, zIndex: 2 } : undefined}
      >
        <span className="text-[11px] text-gray-400 dark:text-gray-600 font-mono">
          {rowIndex + 1}
        </span>
      </td>

      {/* Document name */}
      <td
        className={`${isDocColLocked ? 'bg-surface' : 'bg-surface/[0.55]'} px-3 py-2 border-r border-hairline-soft group/doc`}
        style={{
          width: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
          minWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
          maxWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
          ...(isDocColLocked
            ? { position: 'sticky' as const, left: 60, zIndex: 2, boxShadow: '6px 0 8px -2px rgba(0,0,0,0.08)' }
            : {}),
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {getFileIcon(row.file_type)}
          <Tooltip title={row.file_name} placement="top" arrow enterDelay={400}>
            <button
              onClick={() => handleOpenDocument(row)}
              className="text-xs text-gray-800 dark:text-gray-200 hover:text-info-600 dark:hover:text-info-400 truncate flex-1 min-w-0 text-left transition-colors"
            >
              {row.file_name}
            </button>
          </Tooltip>
          {isRowProcessing && (
            <BrandLoader size="xs" className="!h-3.5 !w-3.5 flex-shrink-0" />
          )}
          {isRowFailed && (
            <Tooltip title={row.processing_error || t('Processing failed')} placement="top" arrow>
              <span><PiWarningLight className="w-3.5 h-3.5 text-danger-400 flex-shrink-0" /></span>
            </Tooltip>
          )}
          {/* Compliance row aggregate indicator */}
          {isComplianceTable && worstConfig && (
            <Tooltip title={t(worstConfig.label)} placement="top" arrow>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium flex-shrink-0 ${worstConfig.badgeBg} ${worstConfig.badgeText}`}>
                <worstConfig.Icon className="w-3 h-3 flex-shrink-0" />
                {t(worstConfig.label)}
              </span>
            </Tooltip>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteDocument(row.id); }}
            className="opacity-0 group-hover/row:opacity-100 p-0.5 rounded hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-all flex-shrink-0"
            title={t('Remove document')}
          >
            <PiXLight className="w-3 h-3 text-gray-400 hover:text-danger-400" />
          </button>
        </div>
      </td>

      {/* Data cells */}
      {columns.map((col: TableColumn) => {
        const cell = row.cells[String(col.id)] || null;
        const isEmpty = !cell || cell.status === 'pending';
        const isError = cell?.status === 'error';
        const isCellSelected = selectedCellRowId === row.id && selectedCellColId === col.id;
        const isComplianceCol = col.column_type === 'compliance';
        // Animate ONLY on a live completed-transition while mounted (prev status
        // existed and wasn't completed). Mounting already-completed (scroll-in)
        // does not animate — no more blue flash on scroll.
        const cellKey = `${row.id}-${col.id}`;
        const prevStatus = prevCellStatuses.current[String(col.id)];
        const isNewlyCompleted = cell?.status === 'completed' && prevStatus != null && prevStatus !== 'completed';
        const compStatus = cell?.compliance_status;
        const compConfig = compStatus ? complianceStatusConfig[compStatus] : null;
        const riskCfg = cell?.risk_level ? riskLevelConfig[cell.risk_level] : null;
        const colWidth = getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH);

        // Compliance heatmap layer: subtle tint covers the whole cell so a
        // column reads as a vertical pattern of green/red/amber/gray at a glance.
        // Selection and error states win over the tint.
        const showRiskStripe =
          isComplianceCol && riskCfg && (compStatus === 'non_compliant' || compStatus === 'needs_review');
        const cellTintClass =
          isCellSelected || isError || !isComplianceCol || !compConfig
            ? ''
            : compConfig.cellTint;

        return (
          <td
            key={cellKey}
            className={`relative px-3 py-2 border-r border-hairline-soft dark:border-white/[0.05] text-xs cursor-pointer transition-colors max-h-[60px] overflow-hidden ${
              isCellSelected
                ? 'bg-info-50 dark:bg-info-900/20 ring-2 ring-inset ring-info-400/50 dark:ring-info-500/40'
                : isError
                  ? 'bg-danger-50/40 dark:bg-danger-900/5'
                  : `${cellTintClass} ${isNewlyCompleted ? 'animate-cell-in' : ''}`
            }`}
            style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
            onClick={() => onSelectCell(row.id, col.id)}
          >
            {/* Risk-level stripe on the left edge — only when there's a risk to flag */}
            {showRiskStripe && (
              <span
                className={`absolute left-0 top-0 bottom-0 w-[3px] ${riskCfg!.stripe}`}
                aria-hidden
              />
            )}

            {isEmpty ? (
              (isActive || isRowProcessing) ? (
                <CellSkeleton />
              ) : (
                <span className="text-gray-300 dark:text-gray-700">—</span>
              )
            ) : isError ? (
              <Tooltip title={t('Error extracting value')} placement="top" arrow>
                <span className="inline-flex items-center gap-1 text-danger-400 italic text-[11px]">
                  <PiWarningLight className="w-3 h-3" />
                  {t('Error')}
                </span>
              </Tooltip>
            ) : isComplianceCol && compConfig ? (
              // Compliance cell: single-line status badge (+ risk + comment count).
              // The source excerpt / AI analysis lives in the tooltip and the cell
              // detail panel so the row stays one line tall.
              <Tooltip title={cell?.ai_analysis || cell?.source_excerpt || t(compConfig.label)} placement="top" arrow enterDelay={300}>
                <span className="inline-flex items-center gap-1 min-w-0">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium flex-shrink-0 ${compConfig.badgeBg} ${compConfig.badgeText}`}>
                    <compConfig.Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    {t(compConfig.label)}
                    {riskCfg && (compStatus === 'non_compliant' || compStatus === 'needs_review') && (
                      <span className={`ml-0.5 text-[9px] font-medium ${riskCfg.color}`}>
                        · {t(riskCfg.label)}
                      </span>
                    )}
                  </span>
                  {(cell?.comment_count ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                      <PiChatTextLight className="w-2.5 h-2.5" />
                      {cell!.comment_count}
                    </span>
                  )}
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={cell?.value || ''} placement="top" arrow enterDelay={400}>
                <span className="flex items-center gap-1">
                  <CellValueRenderer value={cell?.value || null} dataType={col.data_type} />
                  {(cell?.comment_count ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] text-gray-400 dark:text-gray-500 flex-shrink-0 ml-auto">
                      <PiChatTextLight className="w-2.5 h-2.5" />
                      {cell!.comment_count}
                    </span>
                  )}
                </span>
              </Tooltip>
            )}
          </td>
        );
      })}

      {/* Spacer */}
      <td className="border-hairline-soft" />
    </tr>
  );
};
BatchTableRowView.displayName = 'BatchTableRowView';

const MemoBatchTableRowView = React.memo(BatchTableRowView);

// ─── Component ───────────────────────────────────────────────────────────────

const BatchTableDetail: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();
  const { setIsContentScrollable } = useContext(LayoutContext);

  // Redux state
  const table = useSelector((state: RootState) => state.batchTables.currentTable);
  const uploadProgress = useSelector((state: RootState) => state.batchTables.uploadProgress);
  const isUploadingDocs = !!(uploadProgress.active && table && uploadProgress.tableUuid === table.uuid);

  // ─── Adaptive stall detection ────────────────────────────────────────────
  // A run is "stalled" if no new cells have completed for noticeably longer than
  // its own observed pace. We learn the largest gap between progress updates and
  // flag at 3× that, clamped to [2min, 10min] — so slow/large docs get more
  // leeway and fast runs flag quickly. (We track client-side because the AI's
  // raw-SQL progress writes don't bump the table's updated_at.)
  const [isStalled, setIsStalled] = useState(false);
  const stallRef = useRef<{ completed: number; lastChangeAt: number; maxGap: number }>({
    completed: 0, lastChangeAt: Date.now(), maxGap: 0,
  });
  // Reset the stall clock — call when (re)starting a run so a resume isn't
  // instantly judged stalled using the pre-stop timestamp.
  const resetStallClock = useCallback(() => {
    stallRef.current.lastChangeAt = Date.now();
    setIsStalled(false);
  }, []);
  const liveCompleted = table?.cells_completed ?? 0;
  useEffect(() => {
    const s = stallRef.current;
    if (liveCompleted !== s.completed) {
      if (liveCompleted > s.completed) s.maxGap = Math.max(s.maxGap, Date.now() - s.lastChangeAt);
      s.completed = liveCompleted;
      s.lastChangeAt = Date.now();
      setIsStalled(false);
    }
  }, [liveCompleted]);
  // When the table (re)enters an active state, reset the stall clock so a
  // freshly-resumed run gets a full grace window before it can flag stalled.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = table?.status;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if ((status === 'processing' || status === 'queued') && prev !== status) {
      resetStallClock();
    }
  }, [table?.status, resetStallClock]);
  // Snapshot the values the interval needs, updated each render via a ref so the
  // interval itself can be stable (set up once).
  const stallSnapRef = useRef<{ status?: string; total: number; uploading: boolean }>({ status: undefined, total: 0, uploading: false });
  stallSnapRef.current = {
    status: table?.status,
    total: (table?.column_count || 0) * (table?.document_count || 0),
    uploading: isUploadingDocs,
  };
  useEffect(() => {
    const FLOOR_MS = 120_000, CEIL_MS = 600_000;
    const id = setInterval(() => {
      const { status, total, uploading } = stallSnapRef.current;
      const s = stallRef.current;
      const isProc = status === 'processing' || status === 'queued';
      const threshold = Math.min(CEIL_MS, Math.max(FLOOR_MS, s.maxGap * 3));
      setIsStalled(!!(isProc && !uploading && total > 0 && s.completed < total && (Date.now() - s.lastChangeAt) > threshold));
    }, 20_000);
    return () => clearInterval(id);
  }, []);

  const isLoadingDetail = useSelector((state: RootState) => state.batchTables.isLoadingDetail);
  const isUploading = useSelector((state: RootState) => state.batchTables.isUploading);
  const isProcessing = useSelector((state: RootState) => state.batchTables.isProcessing);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  // Redux state — playbook sync
  const isSyncingPlaybook = useSelector((state: RootState) => state.batchTables.isSyncingPlaybook);

  /*
    ─── The surface ───────────────────────────────────────────────────────
    The panes' layout AND the selected cell come from one hook, which is the
    point: "a cell is open" and "the detail pane is visible" used to be two
    pieces of state, and the pane once mounted zero pixels wide because only
    one of them was ever set. `select` opens and raises the pane; closing the
    pane clears the selection. See `surface/layout.ts`.
  */
  const { panels, api: surfaceApi, selection } = useTableWorkspace(tableId);

  /*
    Selecting a cell must NOT be rebuilt on every selection: `surfaceApi`'s
    identity changes with the selection it carries, and this callback is a
    prop on every rendered row. Reading the api out of a ref keeps the row
    memoisation intact for the live PubSub updates it was written for.
  */
  const surfaceApiRef = useRef(surfaceApi);
  surfaceApiRef.current = surfaceApi;
  const handleSelectCell = useCallback((rowId: number, columnId: number) => {
    surfaceApiRef.current.select({
      kind: 'cell',
      id: cellSelectionId(rowId, columnId),
      mode: 'edit',
    });
  }, []);

  /*
    KEEP THE TWO ENDS OF "THE CELL DETAIL IS OPEN" TIED TOGETHER.

    `select` opens the pane and `clearSelection` closes it, but the pane also
    has its own close button (the shell's `onClose` only sets `visible`) and
    its own persisted `visible` flag. Both of those can move one end without
    the other, and each direction shows on screen:

      pane closed, selection kept  → a cell stays ringed in the grid for a
                                     pane that is no longer there
      pane open, no selection      → a returning user's stored layout reopens
                                     the pane onto nothing

    So each drift is corrected towards the state that was actually asked for.
  */
  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  const isDetailPaneOpen = panels.layout.slots.detail?.visible ?? false;
  useEffect(() => {
    if (!isDetailPaneOpen && selection) surfaceApiRef.current.clearSelection();
    else if (isDetailPaneOpen && !selection) panelsRef.current.setVisible('detail', false);
  }, [isDetailPaneOpen, selection]);

  /*
    The selection is an ADDRESS; the records are looked up out of the LIVE
    table on every render. Holding `{ row, column, cell }` objects in state
    made the open pane a photograph: a `cell_completed` event rewrote the row
    in Redux and the detail went on showing the value from the click.
  */
  const selectedCell = useMemo(() => {
    const address = parseCellSelectionId(selection?.id);
    if (!address || !table) return null;
    const row = table.rows.find((r: TableRow) => r.id === address.rowId);
    const column = table.columns.find((c: TableColumn) => c.id === address.columnId);
    if (!row || !column) return null;
    return { row, column, cell: (row.cells[String(column.id)] as TableCell | undefined) || null };
  }, [selection, table]);

  // Deleting the row or the column the pane was showing leaves an address
  // pointing at nothing. Close it rather than hold an empty pane open.
  useEffect(() => {
    if (table && selection && !selectedCell) surfaceApiRef.current.clearSelection();
  }, [table, selection, selectedCell]);

  // Local state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [syncResult, setSyncResult] = useState<SyncPlaybookResult | null>(null);
  const [isDocColLocked, setIsDocColLocked] = useState(false);

  // ─── Virtualization (dependency-free spacer-row windowing) ───────────────
  // Track the scrollable grid wrapper's scrollTop (rAF-throttled) + clientHeight
  // (ResizeObserver) so <tbody> can render only the visible slice of rows.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  // Sensible fallback so the first paint (before the container measures) renders
  // enough rows rather than only OVERSCAN.
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800,
  );

  const handleGridScroll = useCallback(() => {
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = scrollContainerRef.current;
      if (el) setScrollTop(el.scrollTop);
    });
  }, []);

  // Callback ref: measure + observe the grid container the MOMENT it mounts.
  // A plain mount-effect ran while the page was still in its loading state (the
  // container didn't exist yet), so the ResizeObserver was never attached and
  // viewportHeight stayed 0 — rendering only OVERSCAN rows (the "cut" on dev).
  const setScrollContainer = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (el) {
      const fallback = typeof window !== 'undefined' ? window.innerHeight : 800;
      setViewportHeight(el.clientHeight || fallback);
      const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight || fallback));
      ro.observe(el);
      roRef.current = ro;
    }
  }, []);

  useEffect(() => () => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (scrollRafRef.current != null) { cancelAnimationFrame(scrollRafRef.current); scrollRafRef.current = null; }
  }, []);

  // Track whether the initial fetch for THIS tableId has completed
  const hasInitiallyFetched = useRef(false);

  // Track cells that were already completed when the table first loaded,
  // so we only animate newly-completed cells (from PubSub during processing).
  const initialCompletedCells = useRef<Set<string>>(new Set());

  // Column widths for resizing
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizingRef = useRef<{ colKey: string; startX: number; startWidth: number } | null>(null);

  const getColWidth = useCallback((colKey: string, defaultWidth: number) => {
    return columnWidths[colKey] || defaultWidth;
  }, [columnWidths]);

  /**
   * COLUMN resize — the data grid's own gesture, and NOT the surface's.
   *
   * The page now floats panes over this grid, and those panes have two
   * gestures of their own that look superficially like this one: a pane's
   * inner-edge width handle (`panel-resize-*`) and the divider between two
   * stacked panes (`pane-splitter`). They stay separate because they are
   * separate ELEMENTS in separate subtrees, and each press is claimed by
   * exactly one of them:
   *
   *   • The handles below live inside `<th>`, in the CANVAS. The dock columns
   *     are absolutely positioned siblings whose container is
   *     `pointer-events-none`, and the canvas is padded by `readingInset` to
   *     clear whatever is docked — so a pane is never over a column handle
   *     and a column handle is never under a pane.
   *   • `stopPropagation` here means a press on a column handle never reaches
   *     an ancestor; the pane's handle and splitter do the same in the other
   *     direction (`Panel.startResize`, `PaneSplitter.onPointerDown`), so
   *     neither gesture can be started by the other's press.
   *   • Once started, this gesture owns the pointer via `setPointerCapture`,
   *     so dragging a column edge OVER an open pane keeps resizing the column
   *     and cannot hand the drag to the pane underneath.
   *
   * The listeners are also torn down on `pointercancel` and Escape, so a
   * column resize can never end up in the started-and-never-finished state
   * that `usePanelDrag`'s window-level net exists to prevent.
   */
  const handleResizeStart = useCallback((colKey: string, e: ReactPointerEvent<HTMLDivElement>) => {
    // Primary button only — a pen or touch contact reports no button at all,
    // so only a POSITIVE button (right/middle) is rejected.
    if (typeof e.button === 'number' && e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colKey] || (colKey === 'document' ? DEFAULT_DOC_COL_WIDTH : DEFAULT_QUERY_COL_WIDTH);
    resizingRef.current = { colKey, startX, startWidth };

    const handle = e.currentTarget;
    const pointerId = e.pointerId;
    try { handle.setPointerCapture(pointerId); } catch { /* not captureable — the document listeners still cover it */ }

    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const handleMove = (moveEvent: globalThis.PointerEvent) => {
      if (!resizingRef.current) return;
      const delta = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(MIN_COL_WIDTH, resizingRef.current.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current!.colKey]: newWidth }));
    };

    const handleUp = () => {
      resizingRef.current = null;
      document.body.style.userSelect = previousSelect;
      try { handle.releasePointerCapture(pointerId); } catch { /* already released */ }
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleUp);
      window.removeEventListener('keydown', handleKey);
    };

    const handleKey = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') handleUp();
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleUp);
    window.addEventListener('keydown', handleKey);
  }, [columnWidths]);

  // Lock scroll. useLayoutEffect (not useEffect) ensures the outer <main>
  // overflow flips BEFORE the browser paints — otherwise the table grid first
  // measures itself against an `overflow-y-auto` parent (with vertical
  // scrollbar reserving width), then the scroll lock fires and the parent
  // becomes `overflow-hidden`, leaving the inner overflow-x container with a
  // stale scrollWidth that suppresses horizontal scroll until refresh.
  useLayoutEffect(() => {
    setIsContentScrollable(false);
    return () => { setIsContentScrollable(true); };
  }, [setIsContentScrollable]);

  // Track previous tableId to know when we're navigating to a DIFFERENT table
  const prevTableIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const isDifferentTable = prevTableIdRef.current !== tableId;
    prevTableIdRef.current = tableId;

    // Clear stale data only when navigating to a different table
    if (isDifferentTable) {
      hasInitiallyFetched.current = false;
      initialCompletedCells.current = new Set();
      // A cell address from the previous table would resolve to nothing (or,
      // worse, to a different document's row) — close the pane with it.
      surfaceApiRef.current.clearSelection();
      setSelectedRows(new Set());
      setColumnWidths({});
      // Always null-out stale table data so the skeleton shows
      dispatch({ type: 'BATCH_TABLES_SET_DETAIL', payload: null });
    }

    if (tableId) {
      dispatch(fetchBatchTableDetail(tableId) as any).then(() => {
        hasInitiallyFetched.current = true;
      });
    }
  }, [dispatch, tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snapshot completed cells whenever the table data arrives from a full fetch
  // so we know which cells NOT to animate (they were already done).
  useEffect(() => {
    if (!table || !hasInitiallyFetched.current) return;
    // Only snapshot once per table load (not on every PubSub cell update)
    if (initialCompletedCells.current.size > 0) return;
    const completed = new Set<string>();
    for (const row of table.rows) {
      for (const [colId, cell] of Object.entries(row.cells)) {
        if (cell && cell.status === 'completed') {
          completed.add(`${row.id}-${colId}`);
        }
      }
    }
    if (completed.size > 0) {
      initialCompletedCells.current = completed;
    }
  }, [table]);

  // Real-time PubSub: always connected while on the detail page so we never
  // miss events when column re-processing or row processing starts.
  useTablePubSub(tableId, !!table);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'table-detail',
    steps: tableDetailTourSteps,
    readyToShow: !isLoadingDetail && !!table && table.columns.length > 0,
    autoStartDelay: 600,
  });

  // ─── Breadcrumbs ─────────────────────────────────────────────────────

  const breadcrumbs = useMemo(
    () => [
      { name: 'Tables', href: '/tables', current: false },
      { name: table?.name || 'Loading...', href: '', current: true },
    ],
    [table?.name]
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const handleUploadClick = useCallback(() => {
    if (!tableId) return;
    dispatch(showSidePanel({
      type: 'addTableDocument',
      showSidePanel: true,
      size: 'md',
      message: t('Upload Documents'),
      icon: <PiUploadSimpleLight className="w-5 h-5 text-primary-500" />,
      subMsg: t('Add documents to this table for PONS Insight analysis.'),
      tableUuid: tableId,
    }) as any);
  }, [dispatch, tableId, t]);

  const handleAddColumn = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'addTableColumn',
      message: t('Add Column'),
      showModal: true,
      icon: <PiColumnsLight className="w-5 h-5 text-primary-500" />,
      tableUuid: tableId,
      onConfirm: async (data: { name: string; query?: string; data_type: ColumnDataType }) => {
        await dispatch(addColumn(tableId, data) as any);
      },
    }) as any);
  }, [dispatch, tableId, t]);

  const handleStartProcessing = useCallback(() => {
    if (!tableId || !table) return;
    if (!checkAiFeatureGate('tables_ai')) return;

    if (table.column_count === 0 || table.document_count === 0) {
      dispatch(showModal({
        type: 'confirmation',
        message: table.column_count === 0
          ? t('Missing Columns')
          : t('Missing Documents'),
        subMsg: table.column_count === 0
          ? t('Please add at least one column (query) before processing.')
          : t('Please upload at least one document before processing.'),
        showModal: true,
        onConfirm: () => dispatch(hideModal()),
      }) as any);
      return;
    }

    dispatch(showModal({
      type: 'confirmation',
      message: t('Start AI Processing'),
      subMsg: t(`This will process ${table.document_count} document${table.document_count > 1 ? 's' : ''} across ${table.column_count} column${table.column_count > 1 ? 's' : ''}, creating ${table.document_count * table.column_count} AI queries.`),
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        // Clear the snapshot so all newly-completed cells animate
        initialCompletedCells.current = new Set();
        await dispatch(startProcessing(tableId) as any);
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, table]);

  const handleExportCSV = useCallback(() => {
    if (tableId) dispatch(exportTable(tableId, 'csv') as any);
  }, [dispatch, tableId]);

  const handleExportJSON = useCallback(() => {
    if (tableId) dispatch(exportTable(tableId, 'json') as any);
  }, [dispatch, tableId]);

  const handleReset = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t('Reset Table'),
      subMsg: t('This will clear all AI results and return the table to pending status, allowing you to re-process.'),
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        initialCompletedCells.current = new Set();
        await dispatch(resetTable(tableId) as any);
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId]);

  // Stopping is a brief process (the queue drains + in-flight rows wind down);
  // show a transient "Stopping…" state for immediate feedback.
  const [isStopping, setIsStopping] = useState(false);
  const handleStop = useCallback(async () => {
    if (!tableId) return;
    setIsStopping(true);
    try {
      await dispatch(stopProcessing(tableId) as any);
    } finally {
      setIsStopping(false);
    }
  }, [dispatch, tableId]);

  const handleContinue = useCallback(() => {
    if (!tableId) return;
    if (!checkAiFeatureGate('tables_ai')) return;
    // Resuming counts as fresh activity — clear the stall clock immediately so
    // we don't keep showing "looks stalled" while the first row spins up.
    resetStallClock();
    dispatch(continueProcessing(tableId) as any);
  }, [dispatch, tableId, resetStallClock]);

  const handleRetryFailed = useCallback(() => {
    if (!tableId) return;
    if (!checkAiFeatureGate('tables_ai')) return;
    dispatch(retryFailed(tableId) as any);
  }, [dispatch, tableId]);

  const handleDeleteTable = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Table'),
      subMsg: t('This will permanently delete this table and all associated documents. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        const success = await dispatch(deleteBatchTable(tableId) as any);
        if (success) {
          history.push('/tables');
        }
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, history]);

  const handleSaveTemplate = useCallback(() => {
    if (!tableId || !table) return;
    dispatch(showSidePanel({
      type: 'saveTableTemplate',
      showSidePanel: true,
      size: 'md',
      message: t('Save as Template'),
      icon: <PiFloppyDiskLight className="w-5 h-5 text-primary-500" />,
      subMsg: t('Save the current column structure as a reusable template.'),
      tableUuid: tableId,
      defaultName: `${table.name} Template`,
    }) as any);
  }, [dispatch, tableId, table, t]);

  const handleDeleteDocument = useCallback(
    (rowId: number) => {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Remove Document'),
        subMsg: t('This will remove this document and all its associated cells. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteDocument(tableId, rowId) as any);
          setSelectedRows(prev => { const next = new Set(prev); next.delete(rowId); return next; });
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleDeleteSelectedDocuments = useCallback(() => {
    if (selectedRows.size === 0) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t(`Remove ${selectedRows.size} Selected Document${selectedRows.size > 1 ? 's' : ''}`),
      subMsg: t(`This will remove ${selectedRows.size} selected document${selectedRows.size > 1 ? 's' : ''} and all their associated cells. This action cannot be undone.`),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        for (const rowId of Array.from(selectedRows)) {
          await dispatch(deleteDocument(tableId, rowId) as any);
        }
        setSelectedRows(new Set());
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, selectedRows, t]);

  const handleToggleRow = useCallback((rowId: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleToggleAllRows = useCallback((allRowIds: number[]) => {
    setSelectedRows(prev => {
      if (prev.size === allRowIds.length) return new Set();
      return new Set(allRowIds);
    });
  }, []);

  const handleOpenDocument = useCallback(async (row: TableRow) => {
    if (!tableId) return;
    const fileName = row.file_name;
    const ext = (row.file_type || '').toLowerCase();

    // Map extension to MIME content type for the reader
    const contentTypeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ppt: 'application/vnd.ms-powerpoint',
      txt: 'text/plain',
      csv: 'text/csv',
      html: 'text/html',
      htm: 'text/html',
    };
    const contentType = contentTypeMap[ext] || 'application/octet-stream';

    try {
      // Show reader side panel with loading state (same pattern as Sources)
      dispatch(showSidePanel({
        type: 'reader',
        showSidePanel: true,
        size: 'xl',
        loading: true,
        message: fileName,
        subMsg: t('Document Preview'),
        matterId: '',
        sourceType: 'document',
        citation: {
          title: fileName,
          name: fileName,
          contentType,
        },
      }) as any);

      const blob = await streamTableDocument(tableId, row.id);

      // Update reader with the streamed blob (ReaderComponent handles DOCX/PDF/etc.)
      dispatch(showSidePanel({
        type: 'reader',
        showSidePanel: true,
        size: 'xl',
        loading: false,
        message: fileName,
        subMsg: t('Document Preview'),
        matterId: '',
        sourceType: 'document',
        userAccessLevel: 'view',
        citation: {
          title: fileName,
          name: fileName,
          blob: blob,
          blobObject: blob,
          contentType,
        },
      }) as any);
    } catch (err) {
      console.error('Failed to open document:', err);
    }
  }, [dispatch, tableId, t]);

  const handleDeleteColumn = useCallback(
    (columnId: number) => {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Delete Column'),
        subMsg: t('This will delete this column and all associated answers. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteColumn(tableId, columnId) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleEditColumn = useCallback(
    (col: TableColumn) => {
      dispatch(showModal({
        type: 'editTableColumn',
        message: t('Edit Column'),
        showModal: true,
        icon: <PiPencilSimpleLight className="w-5 h-5 text-primary-500" />,
        tableUuid: tableId,
        columnToEdit: col,
        onConfirm: async (payload: UpdateColumnPayload, queryChanged?: boolean) => {
          await dispatch(updateColumn(tableId, col.id, payload) as any);
          dispatch(hideModal());

          if (queryChanged) {
            // Ask the user whether to re-process
            dispatch(showModal({
              type: 'confirmation',
              message: t('Re-process Column'),
              subMsg: t('The query or data type was changed. Re-processing will update all answers for this column across all documents.'),
              showModal: true,
              onConfirm: async () => {
                await dispatch(processColumn(tableId, col.id) as any);
                dispatch(hideModal());
              },
              onCancel: () => dispatch(hideModal()),
            }) as any);
          }
        },
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleSaveName = useCallback(async () => {
    if (editName.trim() && tableId) {
      await dispatch(updateBatchTable(tableId, { name: editName.trim() }) as any);
    }
    setIsEditingName(false);
  }, [dispatch, tableId, editName]);

  const handleSyncPlaybook = useCallback(async () => {
    if (!tableId) return;
    setSyncResult(null);
    const result = await dispatch(syncPlaybook(tableId) as any);
    if (result) {
      setSyncResult(result as SyncPlaybookResult);
    }
  }, [dispatch, tableId]);

  const handleSyncAndReprocess = useCallback(async () => {
    if (!tableId || !table) return;
    setSyncResult(null);
    const rowIds = table.rows.map((r: TableRow) => r.id);
    const result = await dispatch(syncPlaybook(tableId, rowIds) as any);
    if (result) {
      setSyncResult(result as SyncPlaybookResult);
    }
  }, [dispatch, tableId, table]);

  // ─── Header actions ──────────────────────────────────────────────────

  const failedCount = useMemo(
    () => (table?.rows || []).filter((r: TableRow) => r.status === 'failed').length,
    [table?.rows],
  );


  const headerActions = useMemo(() => {
    if (!table) return [];

    const actions = [];

    // "Busy" = actively doing work: streaming an upload OR processing. Drive all
    // the run-state buttons off this so we never show "Run all" while it's busy.
    const isProcessing = table.status === 'processing' || table.status === 'queued';
    const isBusy = isUploadingDocs || isProcessing;
    const canProcess = !isBusy && table.status !== 'stopped'
      && (table.status === 'pending' || table.status === 'failed' || table.status === 'partial');

    if (canProcess) {
      actions.push({
        label: t('Run all'),
        icon: <PiPlayLight className="w-4 h-4" />,
        onClick: handleStartProcessing,
        variant: 'primary' as const,
        disabled: !hasAiFeatureAccess('tables_ai'),
      });
    }

    // Stopping — transient, disabled feedback while the stop request settles.
    if (isStopping) {
      actions.push({
        label: t('Stopping…'),
        icon: <Spinner size="glyph" className="!h-4 !w-4" />,
        onClick: () => {},
        variant: 'outline' as const,
        disabled: true,
      });
    } else if (isBusy && table.status !== 'stopped') {
      // Stop — available whenever the table is busy (uploading and/or processing).
      actions.push({
        label: t('Stop'),
        icon: <PiStopCircleLight className="w-4 h-4" />,
        onClick: handleStop,
        variant: 'outline' as const,
      });
    }

    // Continue — resume a stopped run, or a processing run that looks stalled.
    // Disabled while the doc set is still finishing its upload.
    if (!isStopping && (table.status === 'stopped' || (isStalled && isProcessing))) {
      actions.push({
        label: t('Continue'),
        icon: <PiPlayLight className="w-4 h-4" />,
        onClick: handleContinue,
        variant: 'primary' as const,
        disabled: !hasAiFeatureAccess('tables_ai') || isUploadingDocs,
      });
    }

    // Retry failed — re-run only the failed rows (when not otherwise busy).
    if (failedCount > 0 && !isBusy) {
      actions.push({
        label: t('Retry {{count}} failed', { count: failedCount }),
        icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
        onClick: handleRetryFailed,
        variant: 'outline' as const,
        disabled: !hasAiFeatureAccess('tables_ai'),
      });
    }

    return actions;
  }, [table, handleStartProcessing, handleStop, handleContinue, handleRetryFailed, isStalled, isStopping, isUploadingDocs, failedCount, t]);

  /**
   * The shared manage-access side panel.
   *
   * Lifted out of the overflow menu so the Access pane's own footer button
   * opens the same panel rather than a second copy of this payload — the
   * pane is the READ, this is the WRITE.
   */
  const handleManageAccess = useCallback(() => {
    if (!table) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      resourceUuid: tableId,
      resourceType: 'table',
      collaborators: table.shared_with || [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addTableCollaborator(uuid, userUuid, access) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeTableCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateTableCollaboratorAccess(uuid, collabUuid, access) as any);
      },
      onUpdate: async () => {
        if (tableId) dispatch(fetchBatchTableDetail(tableId) as any);
      },
    }));
  }, [dispatch, t, table, tableId, userInfo?.UserUID]);

  const headerDropdownActions = useMemo(() => {
    if (!table) return [];
    const actions: any[] = [];

    /* NO "Manage Access" HERE — the rail's Access pill (badged with the
       count) raises the pane that lists who this table is shared with, and
       the pane's own button opens the editor. */

    actions.push({
      label: t('Activity'),
      icon: <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
      // The audit log is a PANE now, not a hand-rolled fixed drawer over the
      // grid: it docks, resizes and is remembered like everything else here.
      onClick: () => surfaceApiRef.current.openPane('activity'),
      variant: 'outline' as const,
    });

    if (table.cells_completed > 0) {
      actions.push({
        label: t('Export CSV'),
        icon: <PiDownloadSimpleLight className="w-4 h-4" />,
        onClick: handleExportCSV,
        variant: 'outline' as const,
      });
    }

    if (table.column_count > 0) {
      actions.push({
        label: t('Save as Template'),
        icon: <PiFloppyDiskLight className="w-4 h-4" />,
        onClick: handleSaveTemplate,
        variant: 'outline' as const,
      });
    }

    actions.push({
      label: t('Delete Table'),
      icon: <PiTrashLight className="w-4 h-4 text-danger-500" />,
      onClick: handleDeleteTable,
      variant: 'destructive' as const,
    });

    return actions;
  }, [table, handleDeleteTable, handleReset, handleExportCSV, handleSaveTemplate, t]);

  // ─── Progress bar ────────────────────────────────────────────────────

  // Derive the cell total from the live doc/column counts rather than the stored
  // cells_total. The AI progress events carry a cells_total snapshot from when a
  // batch was queued, which goes stale as more docs stream in (e.g. 6598/6720
  // for 601 docs × 16 cols). document_count stays current as rows arrive, so
  // column_count × document_count is always consistent.
  const cellsTotalDerived = table ? (table.column_count || 0) * (table.document_count || 0) : 0;
  const cellsCompletedClamped = table ? Math.min(table.cells_completed || 0, cellsTotalDerived) : 0;
  const progressPercent = cellsTotalDerived > 0
    ? Math.round((cellsCompletedClamped / cellsTotalDerived) * 100)
    : 0;
  // While documents are still streaming in, never show 100% / "done" — more
  // cells are still coming, so cap the displayed bar at 99%.
  const displayPercent = isUploadingDocs ? Math.min(progressPercent, 99) : progressPercent;

  // ─── Loading / error states ──────────────────────────────────────────

  // Show skeleton when:
  // 1. Loading with no table data yet (initial navigation)
  // 2. Haven't completed the first fetch yet (before useEffect fires)
  const showSkeleton = (!hasInitiallyFetched.current && !table) || (isLoadingDetail && !table);
  if (showSkeleton) {
    return (
      <DetailFrame breadcrumbs={[{ name: 'Tables', href: '/tables', current: false }, { name: 'Loading...', href: '', current: true }]}>
        {/* Skeleton toolbar */}
        <div className="px-4 py-2 flex items-center gap-2 flex-shrink-0 glass !border-x-0 !border-t-0 shadow-sm">
          <div className="h-5 w-20 rounded-full bg-surface-sunken animate-fadePulse" />
          <div className="w-px h-5 bg-hairline mx-1" />
          <div className="h-7 w-28 rounded bg-surface-sunken animate-fadePulse" />
          <div className="h-7 w-24 rounded bg-surface-sunken animate-fadePulse" />
          <div className="flex-1" />
          <div className="h-4 w-16 rounded bg-surface-sunken animate-fadePulse" />
        </div>
        {/* Skeleton table */}
        <div className="flex-1 overflow-hidden">
          <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface border-b border-hairline">
                <th className="bg-surface-sunken w-[36px] px-1 py-3 border-r border-hairline" />
                <th className="bg-surface-sunken w-[32px] px-1 py-3 border-r border-hairline" />
                <th className="bg-surface-sunken px-3 py-3 border-r border-hairline w-[260px]">
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
                </th>
                {[1, 2, 3, 4].map(i => (
                  <th key={i} className="bg-surface px-3 py-3 border-r border-hairline-soft w-[220px]">
                    <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" style={{ width: `${50 + i * 10}%` }} />
                    <div className="h-2 w-3/4 rounded bg-surface-sunken animate-fadePulse mt-1.5" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(row => (
                <tr key={row} className="border-b border-hairline-soft">
                  <td className="bg-surface-sunken/80 px-1 py-3 border-r border-hairline w-[36px]">
                    <div className="h-3.5 w-3.5 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse mx-auto" />
                  </td>
                  <td className="bg-surface-sunken/80 px-1 py-3 border-r border-hairline w-[32px]">
                    <div className="h-3 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse mx-auto" />
                  </td>
                  <td className="bg-surface-sunken/80 px-3 py-3 border-r border-hairline">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse flex-shrink-0" />
                      <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" style={{ width: `${55 + row * 5}%` }} />
                    </div>
                  </td>
                  {[1, 2, 3, 4].map(col => (
                    <td key={col} className="px-3 py-3 border-r border-hairline-soft">
                      <div className="space-y-1.5">
                        <div className="h-3 rounded-full animate-skeleton-shimmer" style={{ width: `${50 + (row + col) * 5}%`, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%' }} />
                        <div className="h-3 rounded-full animate-skeleton-shimmer" style={{ width: `${30 + (row + col) * 4}%`, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animationDelay: '0.15s' }} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailFrame>
    );
  }

  // Not found (loading finished but no table)
  if (!table) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} notFound>
        <div />
      </DetailFrame>
    );
  }

  // This point is only reached when isLoadingDetail is false AND table exists

  const columns = table.columns.slice().sort((a: TableColumn, b: TableColumn) => a.sort_order - b.sort_order);
  const rows = table.rows.slice().sort((a: TableRow, b: TableRow) => a.sort_order - b.sort_order);
  // Treat an in-flight streaming upload as active too, so the "Processing…"
  // indicator (and cell skeletons) stay steady even if the backend status
  // momentarily flips to 'completed' between upload batches.
  // 'stopped' is never "active" — even while the doc set finishes uploading, we
  // don't want cell skeletons; pending cells should render a static "—".
  const isActive = table.status !== 'stopped'
    && (['queued', 'processing'].includes(table.status) || isUploadingDocs);
  // After Stop the backend is immediately 'stopped', but the rows already picked
  // up by worker threads keep finishing for a while. Treat that wind-down as
  // "Stopping…" (data-driven) rather than claiming it's fully stopped.
  const isWindingDown = table.status === 'stopped'
    && (table.rows || []).some((r: TableRow) => r.status === 'processing');
  const repaperBlocked = table.status === 'processing' || table.status === 'failed';

  // ─── Row windowing ──────────────────────────────────────────────────────
  // We window the real rows and the uploading skeleton-placeholders TOGETHER as
  // ONE logical list of length (rows.length + placeholderCount). The placeholders
  // always render AFTER the real rows, so a single contiguous run of fixed-height
  // (ROW_H) rows maps cleanly to scrollTop. This lets us uncap the placeholders
  // (no Math.min) since only the visible window is ever rendered.
  const placeholderCount = isUploadingDocs
    ? Math.max(0, uploadProgress.total - rows.length)
    : 0;
  const totalRowCount = rows.length + placeholderCount;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  // Only window genuinely large tables. Small/medium tables render in full at
  // their NATURAL row heights (no fixed-height forcing, no spacers) — avoids any
  // ROW_H estimate mismatch, and memoization keeps it fast.
  const virtualized = totalRowCount > VIRTUALIZE_THRESHOLD;
  const windowStart = virtualized ? clamp(Math.floor(scrollTop / ROW_H) - OVERSCAN, 0, totalRowCount) : 0;
  const windowEnd = virtualized ? clamp(Math.ceil((scrollTop + viewportHeight) / ROW_H) + OVERSCAN, 0, totalRowCount) : totalRowCount;
  const topSpacerH = virtualized ? windowStart * ROW_H : 0;
  const bottomSpacerH = virtualized ? (totalRowCount - windowEnd) * ROW_H : 0;
  // Real rows occupy logical indices [0, rows.length); placeholders [rows.length, totalRowCount).
  const realRowStart = clamp(windowStart, 0, rows.length);
  const realRowEnd = clamp(windowEnd, 0, rows.length);
  const placeholderStart = clamp(windowStart - rows.length, 0, placeholderCount);
  const placeholderEnd = clamp(windowEnd - rows.length, 0, placeholderCount);
  const selectedCellRowId = selectedCell ? selectedCell.row.id : null;
  const selectedCellColId = selectedCell ? selectedCell.column.id : null;

  return (<>
    <TableSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={headerActions}
      dropdownActions={headerDropdownActions}
      /* `DetailFrame`'s own stack is decorative — not pressable — so the
         faces are an `additionalHeaderElement` that RAISES THE ACCESS PANE. */
      collaborators={[]}
      additionalHeaderElements={
        <>
          <ProjectBadges resourceType="table" resourceUuid={tableId} />
          <PendingAccessRequests resourceType="table" resourceUuid={tableId} />
          <CollaboratorStack
            collaborators={table.shared_with || []}
            onClick={() => surfaceApiRef.current.openPane('access')}
            testId="table-collaborator-stack"
          />
        </>
      }
    >
      <div className="flex-1 flex flex-col min-h-0">
      {/*
        THE TOOLBAR AND THE BANNERS ARE THEIR OWN ROWS, ABOVE THE SURFACE.

        They are the frame around the work, not part of it — out here they
        span the window and a docked pane floats below them instead of sitting
        over them or shrinking them. It is also where the `PaneRail` lives:
        this page has no tab bar to pin it to, and the rail is the only way
        back to a pane that has been closed.
      */}
      {/* Toolbar */}
      <div data-tour-id="table-detail-toolbar" className="px-4 py-2 flex items-center gap-3 flex-shrink-0 glass !border-x-0 !border-t-0 shadow-sm">
        {/* LEFT — stats + progress */}
        <div className="flex items-center gap-3 min-w-0">
          {isUploadingDocs ? (
            <span className={`text-[11px] whitespace-nowrap tabular-nums flex items-center gap-1.5 ${table.status === 'stopped' ? 'text-warning-600 dark:text-warning-400' : 'text-info-600 dark:text-info-400'}`}>
              <Spinner size="glyph" className="!h-3.5 !w-3.5 flex-shrink-0" />
              {table.status === 'stopped' ? t('Finishing upload') : t('Uploading')} {Math.min(uploadProgress.uploaded, uploadProgress.total)}/{uploadProgress.total} doc{uploadProgress.total !== 1 ? 's' : ''} · {table.column_count} col{table.column_count !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap tabular-nums">
              {table.document_count} doc{table.document_count !== 1 ? 's' : ''} · {table.column_count} col{table.column_count !== 1 ? 's' : ''}
            </span>
          )}

          {cellsTotalDerived > 0 ? (
            <div className="flex items-center gap-2">
              <div className="w-28 h-1.5 bg-hairline rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${displayPercent === 100 ? 'bg-success-400' : 'bg-info-500'}`}
                  style={{ width: `${displayPercent}%` }}
                />
              </div>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono tabular-nums whitespace-nowrap">
                {cellsCompletedClamped}/{cellsTotalDerived}
              </span>
            </div>
          ) : (
            <Badge color={statusColorMap[table.status] || 'gray'}>
              {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
            </Badge>
          )}

          {isActive && !isStalled && (
            <div className="flex items-center gap-1.5 text-info-500">
              <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
              <span className="text-[11px] font-medium">{t('Processing…')}</span>
            </div>
          )}

          {isUploading && (
            <div className="flex items-center gap-1.5 text-warning-500">
              <Spinner size="glyph" className="!h-3.5 !w-3.5" />
              <span className="text-[11px] font-medium">{t('Uploading…')}</span>
            </div>
          )}

          {(isStopping || isWindingDown) && (
            <div className="flex items-center gap-1.5 text-warning-600 dark:text-warning-400">
              <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
              <span className="text-[11px] font-medium">
                {t('Stopping…')}
                {isWindingDown && (table.rows || []).filter((r: TableRow) => r.status === 'processing').length > 0
                  ? ` (${(table.rows || []).filter((r: TableRow) => r.status === 'processing').length} finishing)`
                  : ''}
              </span>
            </div>
          )}

          {!isStopping && !isWindingDown && table.status === 'stopped' && !isUploadingDocs && (
            <div className="flex items-center gap-1.5 text-warning-600 dark:text-warning-400">
              <PiStopCircleLight className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium">{t('Stopped — Continue to resume')}</span>
            </div>
          )}

          {isStalled && table.status !== 'stopped' && (
            <div className="flex items-center gap-1.5 text-warning-600 dark:text-warning-400">
              <PiWarningLight className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium">{t('Looks stalled — use Continue')}</span>
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* RIGHT — selection actions + primary actions */}
        {selectedRows.size > 0 && (
          <>
            <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">
              {selectedRows.size} {t('selected')}
            </span>
            {table.mode === 'compliance' && (
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  if (repaperBlocked) return;
                  // The pane reads the row picks off `selectedRows` directly,
                  // so there is nothing to hand it here.
                  surfaceApi.openPane('bulkReview');
                }}
                icon={<PiShieldCheckLight className="w-3.5 h-3.5" />}
                disabled={isProcessing || isUploading || repaperBlocked || !hasAiFeatureAccess('tables_ai')}
                title={
                  repaperBlocked
                    ? (table.status === 'processing'
                        ? t('Available once processing finishes')
                        : t('Not available for failed tables'))
                    : undefined
                }
              >
                {t('Review for Repaper')}
              </Button>
            )}
            <Button variant="destructive" size="small" onClick={handleDeleteSelectedDocuments} icon={<PiTrashLight className="w-3.5 h-3.5" />}>
              {t('Delete')}
            </Button>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1.5 py-1 rounded hover:bg-surface-sunken/70 transition-colors"
            >
              {t('Clear')}
            </button>
            <div className="w-px h-5 bg-hairline mx-0.5" />
          </>
        )}

        <Button variant="outline" size="small" onClick={handleUploadClick} icon={<PiUploadSimpleLight className="w-3.5 h-3.5" />}>
          {t('Add documents')}
        </Button>
        <Button variant="outline" size="small" onClick={handleAddColumn} icon={<PiColumnsLight className="w-3.5 h-3.5" />}>
          {t('Add column')}
        </Button>

        {/* Bulk Review for Repaper — compliance tables only, no rows selected */}
        {table.mode === 'compliance' && table.cells_completed > 0 && selectedRows.size === 0 && (
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              if (repaperBlocked) return;
              surfaceApi.openPane('bulkReview');
            }}
            icon={<PiShieldCheckLight className="w-3.5 h-3.5" />}
            disabled={isProcessing || isUploading || repaperBlocked || !hasAiFeatureAccess('tables_ai')}
            title={
              repaperBlocked
                ? (table.status === 'processing'
                    ? t('Available once processing finishes')
                    : t('Not available for failed tables'))
                : undefined
            }
          >
            {t('Review for Repaper')}
          </Button>
        )}

        {/* The other half of every pane's close button — see `PaneRail`. */}
        <div className="w-px h-5 bg-hairline mx-0.5" />
        <PaneRail
          panels={panels}
          ariaLabel={t('Panels')}
          labels="auto"
          /* Straight from the surface config, so a pane that asks for a rail
             toggle cannot be added and then forgotten here. */
          items={TABLE_SURFACE.railItems({
            t,
            icons: {
              access: <PiUsersLight />,
              activity: <PiClockCounterClockwiseLight />,
              aiUsage: <PiSparkleLight />,
            },
            // The share list's own size, so the pill is right whether or not
            // the pane has ever been opened.
            badges: { access: table.shared_with?.length || 0 },
          })}
        />
      </div>

      {/* Compliance mode banner */}
      {table.mode === 'compliance' && table.playbook_name && (
        <div className="px-4 py-2 glass !border-x-0 !border-t-0 flex items-center gap-2 flex-shrink-0">
          <PiShieldCheckLight className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
          <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
            {t('Compliance mode')}
          </span>
          <span className="text-xs text-primary-500 dark:text-primary-400">·</span>
          <span className="text-xs text-primary-700 dark:text-primary-300 font-semibold">
            {table.playbook_name}
          </span>
          {table.playbook_snapshot_at && (
            <span className="text-xs text-primary-400 dark:text-primary-500 ml-1">
              ({t('snapshot')} {formatDateTime(table.playbook_snapshot_at)})
            </span>
          )}
          {table.jurisdiction && (
            <>
              <span className="text-xs text-primary-500 dark:text-primary-400">·</span>
              <span className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                <PiGlobeLight className="w-3.5 h-3.5" />
                {table.jurisdiction}
              </span>
            </>
          )}
          <div className="flex-1" />
          {/* inherit_collaborators toggle */}
          <Tooltip
            title={t(table.inherit_collaborators
              ? 'Deep dive reviews are auto-shared with table collaborators. Click to disable.'
              : 'Deep dive reviews are NOT shared with table collaborators. Click to enable.'
            )}
            placement="left"
            arrow
          >
            <button
              onClick={() => {
                if (tableId) {
                  // updateBatchTable merges the change into local state; no refetch needed.
                  dispatch(updateBatchTable(tableId, { inherit_collaborators: !table.inherit_collaborators }) as any);
                }
              }}
              className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full border transition-colors ${
                table.inherit_collaborators
                  ? 'border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 bg-primary-100/50 dark:bg-primary-900/30'
                  : 'border-hairline text-gray-500 dark:text-gray-400 bg-surface'
              }`}
            >
              <PiUsersLight className="w-3 h-3" />
              {t('Auto-share')}
              {table.inherit_collaborators ? (
                <span className="w-1.5 h-1.5 rounded-full bg-success-500 flex-shrink-0" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
              )}
            </button>
          </Tooltip>
        </div>
      )}

      {/* Playbook drift warning */}
      {table.playbook_drift && (
        <div className="px-4 py-2.5 bg-warning-50/70 dark:bg-warning-900/20 border-b border-hairline flex items-center gap-3 flex-shrink-0">
          <PiWarningLight className="w-4 h-4 text-warning-600 dark:text-warning-400 flex-shrink-0" />
          <span className="text-xs text-warning-700 dark:text-warning-300 flex-1">
            {t('The linked playbook has been updated since this table was created.')}
          </span>
          {syncResult ? (
            <span className="text-xs text-warning-700 dark:text-warning-300 font-medium">
              {t('Synced: {{added}} added, {{detached}} detached', {
                added: syncResult.columns_added,
                detached: syncResult.columns_detached,
              })}
            </span>
          ) : null}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="neutral"
              size="small"
              onClick={handleSyncPlaybook}
              disabled={isSyncingPlaybook}
              icon={<PiArrowsClockwiseLight className={`w-3.5 h-3.5 ${isSyncingPlaybook ? 'animate-spin' : ''}`} />}
            >
              {isSyncingPlaybook ? t('Syncing…') : t('Sync Columns')}
            </Button>
            {syncResult && syncResult.columns_added > 0 && table.rows.length > 0 && (
              <Button
                variant="primary"
                size="small"
                onClick={handleSyncAndReprocess}
                disabled={isSyncingPlaybook}
              >
                {t('Reprocess All Rows')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/*
        THE SURFACE. The grid is the canvas; the cell detail, the repaper
        review and the audit log float over its right edge.
      */}
      <DetailPageSurface
        surface={TABLE_SURFACE}
        layoutApi={panels}
        /*
          FULL BLEED. The grid itself had already stopped being a card — no
          glass, no border, no radius — and it still read as one, because the
          box around it was not the grid's: the shell insets its canvas by
          DOCK_INSET on all four sides, so the spreadsheet sat 12px in from
          every edge with the page background showing around it.

          A spreadsheet IS the surface. It runs to the edge, and the frozen
          first column and sticky header need to reach the edge to do their
          job. Only the sides with a pane docked keep their inset — the cell
          detail must not sit flush against the cells it describes.
        */
        canvasBleed
        panes={[
          {
            id: 'detail',
            title: selectedCell ? selectedCell.column.name : t('Cell'),
            icon: <PiTargetLight />,
            /*
              A THUNK: only built while the pane is on screen, and rebuilt on
              every render where it is — so a `cell_completed` event reaches
              the open pane.

              Keyed by cell: the panel derives sources and history internally,
              and switching cells without a remount would show the previous
              cell's derived state until the new fetch resolved.
            */
            body: () => (selectedCell ? (
              <CellDetailPanel
                key={`${selectedCell.row.id}-${selectedCell.column.id}`}
                docked
                cell={selectedCell.cell}
                column={selectedCell.column}
                row={selectedCell.row}
                tableUuid={tableId}
                onClose={surfaceApi.clearSelection}
              />
            ) : null),
          },
          /*
            Compliance only, and passed CONDITIONALLY rather than hidden: the
            shell draws the panes a page gives it, so on an extraction table
            this pane id simply never renders — no empty pane, no dead pill.
          */
          ...(table.mode === 'compliance' ? [{
            id: 'bulkReview' as const,
            title: t('Review for Repaper'),
            icon: <PiShieldCheckLight />,
            body: () => (
              <BulkReviewPanel
                /*
                  Re-seeded from the page's live row picks. A dock move
                  REMOUNTS a pane body, so the pane's own tick-boxes are its
                  short-lived state and `selectedRows` — which belongs to the
                  page — is what survives.
                */
                key={`bulk-${Array.from(selectedRows).sort().join(',')}`}
                table={table}
                preselectedRowIds={Array.from(selectedRows)}
                onClose={() => panels.setVisible('bulkReview', false)}
              />
            ),
          }] : []),
          {
            id: 'access',
            title: t('Access'),
            icon: <PiUsersLight />,
            body: (
              <AccessPane
                collaborators={table.shared_with || []}
                onManageAccess={handleManageAccess}
                canManage
              />
            ),
          },
          {
            id: 'activity',
            title: t('Activity'),
            icon: <PiClockCounterClockwiseLight />,
            // A thunk: the audit log fetches on mount, so a table whose
            // activity is never opened never asks for it.
            body: () => (
              <AuditLogPanel
                entityType="table"
                entityId={tableId}
                entityName={table.name || ''}
                onClose={() => panels.setVisible('activity', false)}
              />
            ),
          },
          {
            id: 'aiUsage',
            title: t('AI usage'),
            icon: <PiSparkleLight />,
            // A thunk, for the same reason as the audit log: the panel
            // fetches on mount, so a table nobody asks the cost of never
            // costs a request.
            body: () => (
              <AIUsagePanel
                entityType="table"
                entityId={tableId}
                entityName={table.name || ''}
                onClose={() => panels.setVisible('aiUsage', false)}
              />
            ),
          },
        ]}
      >
      {/* Grid View — the canvas. `flex-1 min-h-0` claims the space the shell
          has already computed; the `calc(100vh - …)` cap this replaced was a
          guess at the frame's height and was wrong on some viewport.
          The grid is NOT a card: no glass, no border, no radius, no shadow. A
          spreadsheet reads as the surface itself, and an opaque page
          background is what lets the frozen column and header actually hide
          the cells scrolling under them. */}
      <div
        ref={setScrollContainer}
        onScroll={handleGridScroll}
        className="flex-1 min-h-0 min-w-0 custom-scrollbar-main relative bg-bg"
        style={{ overflowX: 'auto', overflowY: 'auto' }}
      >
        {/* Tour anchor for the grid area */}
        <div
          data-tour-id="table-detail-grid"
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: 120 }}
        />
        {columns.length > 0 && rows.length === 0 && !isUploadingDocs ? (
          /*
            COLUMNS BUT NO DOCUMENTS — the state this screen exists to get out
            of, so it gets the whole surface rather than a line of text in a
            `<td>` under an empty header row. See `TableEmptyState`.

            The grid is not rendered at all here: with no rows there is nothing
            under the headers, and "Add column" / "Add documents" both live in
            the toolbar above, so nothing becomes unreachable. The moment a row
            exists — including the optimistic ones the upload appends — this is
            gone and the grid is back untouched.
          */
          <TableEmptyState
            tableUuid={tableId!}
            columns={columns}
            playbookName={table.playbook_name}
            canRun={hasAiFeatureAccess('tables_ai')}
            runDisabledTooltip={t('Upgrade your plan to use AI Tables.')}
          />
        ) : columns.length === 0 && rows.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-surface-sunken border border-hairline flex items-center justify-center mb-5">
              <PiTableLight className="w-8 h-8 text-gray-300 dark:text-gray-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {t("Let's set up your table")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
              {t('Add columns to define your AI queries, then upload documents for analysis.')}
            </p>
            <div className="flex gap-3">
              <Button variant="primary" size="small" onClick={handleAddColumn} icon={<PiColumnsLight />}>
                {t('Add Column')}
              </Button>
              <Button variant="outline" size="small" onClick={handleUploadClick} icon={<PiUploadSimpleLight />}>
                {t('Upload Documents')}
              </Button>
            </div>
          </div>
        ) : (
          /* Spreadsheet grid */
          <div className="inline-block min-w-full">
            {/* tableLayout:'fixed' lets the colgroup widths drive the table's
                natural width. Do NOT add minWidth:'100%' here — combined with
                the inline-block parent's min-w-full, it can pin the table to
                exactly the container width even when the colgroup wants more,
                suppressing horizontal scroll. The inline-block min-w-full
                already keeps the empty-table state filling the viewport. */}
            <table className="border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 32 }} />
                <col style={{ width: getColWidth('document', DEFAULT_DOC_COL_WIDTH) }} />
                {columns.map((col: TableColumn) => (
                  <col key={col.id} style={{ width: getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH) }} />
                ))}
                <col style={{ width: 40 }} />
              </colgroup>

              {/* ── Column headers ── */}
              <thead data-tour-id="table-detail-columns" className="sticky top-0 z-10">
                <tr className="bg-surface border-b border-hairline shadow-sm">
                  {/*
                    Select-all — `px-1`, EXACTLY as the body's checkbox cell.

                    This was `px-3` against the body's `px-1`, and since both
                    cells are 36px wide and start at the same x, that 8px of
                    extra padding pushed the header's checkbox 8px right of
                    every checkbox under it. Same column, two paddings, one
                    visibly crooked line down the left edge of the grid.
                  */}
                  <th
                    className="bg-surface text-center px-1 py-2.5 border-r border-hairline-soft dark:border-white/[0.07] select-none w-[36px]"
                    style={isDocColLocked ? { position: 'sticky', left: 0, top: 0, zIndex: 30 } : undefined}
                  >
                    <CheckBox
                      label={t('Select all')}
                      hideLabel
                      checked={rows.length > 0 && selectedRows.size === rows.length}
                      onChange={() => handleToggleAllRows(rows.map(r => r.id))}
                    />
                  </th>

                  {/* Row # — same story, same fix: the body's cell is `px-1`. */}
                  <th
                    className="bg-surface text-center px-1 py-2.5 border-r border-hairline-soft dark:border-white/[0.07] select-none w-[32px]"
                    style={isDocColLocked ? { position: 'sticky', left: 36, top: 0, zIndex: 30 } : undefined}
                  />

                  {/* Document header */}
                  <th
                    className="bg-surface text-left px-3 py-2.5 border-r border-hairline-soft dark:border-white/[0.07] select-none relative"
                    style={{
                      width: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      minWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      maxWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      ...(isDocColLocked
                        ? { position: 'sticky' as const, left: 60, top: 0, zIndex: 30, boxShadow: '6px 0 8px -2px rgba(0,0,0,0.08)' }
                        : {}),
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <PiFileTextLight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
                        {t('Document')}
                      </span>
                      <Tooltip title={isDocColLocked ? t('Unlock document column') : t('Lock document column')} placement="top" arrow>
                        <button
                          onClick={() => setIsDocColLocked((v) => !v)}
                          aria-pressed={isDocColLocked}
                          className={`ml-auto mr-2 p-1 rounded transition-colors flex-shrink-0 ${
                            isDocColLocked
                              ? 'text-info-600 bg-info-50 dark:text-info-300 dark:bg-info-900/20'
                              : 'text-gray-400 hover:text-fg-body hover:bg-surface-sunken'
                          }`}
                        >
                          {isDocColLocked ? <PiLockLight className="w-3.5 h-3.5" /> : <PiLockOpenLight className="w-3.5 h-3.5" />}
                        </button>
                      </Tooltip>
                    </div>
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-info-400 active:bg-info-500 z-30"
                      onPointerDown={(e) => handleResizeStart('document', e)}
                    />
                  </th>

                  {/* Query column headers */}
                  {columns.map((col: TableColumn) => {
                    const colWidth = getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH);
                    return (
                    <th
                      key={col.id}
                      className="bg-surface text-left px-3 py-2.5 border-r border-hairline-soft group relative select-none"
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {col.column_type === 'compliance'
                            ? <PiShieldCheckLight className="w-3.5 h-3.5 text-primary-500" />
                            : (columnTypeIcon[col.data_type] || <PiDiamondLight className="w-3.5 h-3.5" />)
                          }
                        </span>
                        <Tooltip title={col.name} placement="top" arrow enterDelay={300}>
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 truncate tracking-wide">
                            {col.name}
                          </span>
                        </Tooltip>
                        <button
                          onClick={() => handleEditColumn(col)}
                          className="opacity-0 group-hover:opacity-100 ml-auto p-0.5 rounded hover:bg-info-50 dark:hover:bg-info-900/20 transition-all flex-shrink-0"
                          title={t('Edit column')}
                        >
                          <PiPencilSimpleLight className="w-3 h-3 text-info-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteColumn(col.id)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-all flex-shrink-0"
                          title={t('Delete column')}
                        >
                          <PiXLight className="w-3 h-3 text-danger-400" />
                        </button>
                      </div>
                      {col.column_type === 'compliance' && col.risk_weight != null ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${riskWeightConfig(col.risk_weight)}`}>
                            {t('Weight')} {col.risk_weight}
                          </span>
                        </div>
                      ) : col.query ? (
                        <Tooltip title={col.query} placement="bottom" arrow enterDelay={300}>
                          <p className="text-[10px] font-normal text-gray-400 dark:text-gray-600 truncate mt-0.5 leading-tight">
                            {col.query}
                          </p>
                        </Tooltip>
                      ) : null}
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-info-400 active:bg-info-500 z-30"
                        onPointerDown={(e) => handleResizeStart(`col-${col.id}`, e)}
                      />
                    </th>
                    );
                  })}

                  {/* Add column */}
                  <th className="bg-surface px-2 py-2.5 w-10">
                    <Tooltip title={t('Add column')} placement="top" arrow>
                      <button
                        onClick={handleAddColumn}
                        className="p-1 rounded-md hover:bg-surface-sunken transition-colors"
                      >
                        <PiPlusLight className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </Tooltip>
                  </th>
                </tr>
              </thead>

              {/* ── Rows ── */}
              <tbody className="divide-y divide-white/60 dark:divide-white/[0.06]">
                {/* Top spacer — accounts for rows above the window */}
                {topSpacerH > 0 && (
                  <tr aria-hidden style={{ height: topSpacerH }}>
                    <td colSpan={columns.length + 4} style={{ height: topSpacerH, padding: 0, border: 0 }} />
                  </tr>
                )}

                {/* Visible real rows (memoized) */}
                {rows.slice(realRowStart, realRowEnd).map((row: TableRow, localIndex: number) => {
                  const rowIndex = realRowStart + localIndex;
                  return (
                    <MemoBatchTableRowView
                      key={row.id}
                      row={row}
                      rowIndex={rowIndex}
                      columns={columns}
                      isComplianceTable={table.mode === 'compliance'}
                      isActive={isActive}
                      isDocColLocked={isDocColLocked}
                      isSelected={selectedRows.has(row.id)}
                      selectedCellRowId={selectedCellRowId}
                      selectedCellColId={selectedCellColId}
                      getColWidth={getColWidth}
                      handleToggleRow={handleToggleRow}
                      handleOpenDocument={handleOpenDocument}
                      handleDeleteDocument={handleDeleteDocument}
                      onSelectCell={handleSelectCell}
                      t={t}
                      rowHeight={ROW_H}
                    />
                  );
                })}

                {/* Uploading placeholders — the documents still arriving show as
                    skeleton rows (name + cells), resolving to real rows as each
                    batch lands. Uncapped (no Math.min): only the visible window
                    is rendered, and placeholders share the same fixed-height
                    spacer math as the real rows (one logical list). */}
                {isUploadingDocs && Array.from({ length: placeholderEnd - placeholderStart }).map((_, localIndex) => {
                  const i = placeholderStart + localIndex;
                  return (
                  <tr key={`upl-skel-${i}`} style={{ height: ROW_H }} className="bg-surface/[0.55]">
                    <td className="px-1 py-2 border-r border-hairline-soft w-[36px]" />
                    <td className="px-1 py-2 border-r border-hairline-soft text-center w-[32px]">
                      <span className="text-[11px] text-gray-300 dark:text-gray-700 font-mono">{rows.length + i + 1}</span>
                    </td>
                    <td className="px-3 py-2 border-r border-hairline-soft">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-4 h-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse flex-shrink-0" />
                        <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse flex-1" style={{ maxWidth: 160 }} />
                      </div>
                    </td>
                    {columns.map((col: TableColumn) => (
                      <td key={col.id} className="px-3 py-2 border-r border-hairline-soft">
                        <CellSkeleton />
                      </td>
                    ))}
                    <td className="border-hairline-soft" />
                  </tr>
                  );
                })}

                {/* Bottom spacer — accounts for rows below the window */}
                {bottomSpacerH > 0 && (
                  <tr aria-hidden style={{ height: bottomSpacerH }}>
                    <td colSpan={columns.length + 4} style={{ height: bottomSpacerH, padding: 0, border: 0 }} />
                  </tr>
                )}

                {/* No empty-row prompt here any more: that case never reaches
                    the grid — it renders `TableEmptyState` instead, above. */}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </DetailPageSurface>
      </div>
    </DetailFrame>
    </TableSurfaceProvider>
    <FeatureTour
      steps={tableDetailTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  </>);
};

export default BatchTableDetail;